import { useMemo } from "react";

import { uniqBy } from "lodash";

import { type SalesMetricRow } from "@north-beam/nb-common";

import { type TableState } from "../types";

import { useTableStateDimensions } from "./use-table-state-dimensions";

interface Props {
  tableState: TableState;
  row: SalesMetricRow;
}

export const useTableRowFilters = ({ tableState, row }: Props) => {
  const { breakdownDimension, dimensionsToSelect } =
    useTableStateDimensions(tableState);

  return useMemo(() => {
    const breakdownFilters = [];
    const campaignFilters = [];
    const adsetFilters = [];
    const adFilters = [];
    const isSummary = breakdownDimension.getValue(row) === undefined;
    let advancedSearch = null;

    if (tableState.selectedTab === "breakdown") {
      breakdownFilters.push(...tableState.breakdownFilters);

      if (breakdownDimension.getValue(row)) {
        advancedSearch = {
          "==": [
            { var: breakdownDimension.id },
            breakdownDimension.getValue(row),
          ],
        };
      }
    }

    if (["campaign", "adset", "ad"].includes(tableState.selectedTab)) {
      const selection = tableState.breakdownSelection.map((e) => ({
        breakdown: breakdownDimension.name,
        value: e.id,
      }));

      breakdownFilters.push(...selection);

      for (const filter of tableState.breakdownFilters) {
        if (!selection.some((e) => e.breakdown === filter.breakdown)) {
          breakdownFilters.push(filter);
        }
      }
    }

    if (["adset", "ad"].includes(tableState.selectedTab)) {
      campaignFilters.push(...tableState.campaignSelection.map((e) => e.id));
    }

    if (tableState.selectedTab === "ad") {
      adsetFilters.push(...tableState.adsetSelection.map((e) => e.id));
    }

    if (row.adKey) {
      if (tableState.selectedTab === "campaign") {
        campaignFilters.push(row.adKey);
      }

      if (tableState.selectedTab === "adset") {
        adsetFilters.push(row.adKey);
      }

      if (tableState.selectedTab === "ad") {
        adFilters.push(row.adKey);
      }
    }

    return {
      dimensionIds: dimensionsToSelect.map((e) => e.id),
      metricFilters: isSummary ? tableState.metricFilters : undefined,
      breakdownFilters: uniqBy(
        breakdownFilters,
        (e) => `${e.breakdown}${e.value}`,
      ),
      advancedSearch,
      campaignFilters,
      adsetFilters,
      adFilters,
      isSummary,
    };
  }, [tableState, row, breakdownDimension, dimensionsToSelect]);
};
